import {
  ButtonThemes,
  Checkbox,
  CheckCircleIcon,
  Col,
  Collapse,
  ControlLabel,
  ControlLabelPosition,
  Input,
  Modal,
  ModalSizes,
  Row,
  ThreeDotsLoader,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import api, { VideoResponse } from 'api/Assessor/video';
import LayoutContext from 'components/Layout/LayoutContext';
import EditableVideoItem from 'components/__ASSESSOR__/EditableVideoItem/EditableVideoItem';
import { isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import {
  DEFAULT_VALID,
  DEFAULT_VALIDATION_MODEL,
} from 'helpers/utils/ValidationUtils';
import translation from 'locales/translation';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  assembleiaAssessorActionsEditVideo,
  assembleiaAssessorActionsSaveVideo,
} from 'store/modules/AssembleiasAssessorActions';
import {
  VideoAssemblyValidator,
  VideoAssemblyValidatorType,
} from './VideoAssemblyValidator';

interface IProps extends ModalProps {
  eventId: number;
  onConfirm: () => Promise<void>;
  registering?: boolean;
  shouldUseBff: boolean;
  item?: {
    idVideo: number;
    url: string;
    descricao: string | null;
    flagDisponivelAssociado: boolean;
  };
}

const attachmentPrefix =
  translation.Assessor.Evento.Cadastro.Etapas.Anexo.Modal.Videos;

export const VideoAssembly = (props: IProps) => {
  const [url, setUrl] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [videos, setVideos] = useState<VideoResponse[]>([]);
  const [validator, setValidator] = useState<VideoAssemblyValidatorType | null>(
    null
  );

  const dispatchAction = useDispatch();
  const { showToast } = useContext(LayoutContext);

  useEffect(() => {
    if (props.item) {
      setUrl(props.item.url);
      setDescription(props.item.descricao! ?? '');
      setChecked(props.item.flagDisponivelAssociado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValidator(VideoAssemblyValidator(description, url));
  }, [description, url]);

  useEffect(() => {
    if (validator && validator.allIsValid()) {
      setSent(false);
    }
  }, [validator, validator?.allIsValid]);

  const handleSubmit = useCallback(
    async () => {
      if (!sent) setSent(true);
      if (!validator || !validator.allIsValid()) return;

      setLoadingButton(true);

      if (!props.item) {
        dispatchAction(
          assembleiaAssessorActionsSaveVideo(
            {
              idEvento: props.eventId,
              url: url,
              flagDisponivelAssociado: checked,
              descricao: description,
            },
            props.registering ? props.shouldUseBff : false,
            () => {
              if (!props.registering) {
                getVideos().then(() => {
                  setSent(false);
                  setLoadingButton(false);
                  setUrl('');
                  setDescription('');
                  setChecked(false);
                });
              } else {
                props.onConfirm().finally(() => {
                  setLoadingButton(false);
                  props.onClose!(props.modalKey);
                });
              }
            },
            (error) => {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label: languageValues.toasts.error.keyErrors(error.message),
              });
              setLoadingButton(false);
            }
          )
        );
      } else {
        dispatchAction(
          assembleiaAssessorActionsEditVideo(
            {
              idEvento: props.eventId,
              idVideo: props.item!?.idVideo!,
              flagDisponivelAssociado: checked,
              descricao: description,
              url: url,
            },
            props.registering ? props.shouldUseBff : false,
            () => {
              props.onConfirm().finally(() => {
                setLoadingButton(false);
                props.onClose!(props.modalKey);
              });
            },
            (error) => {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label: languageValues.toasts.error.keyErrors(error.message),
              });
              setLoadingButton(false);
            }
          )
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchAction, sent, validator, checked]
  );

  const getVideos = useCallback(async () => {
    setLoading(true);
    const { fail, data } = await api.listar(props.eventId);
    if (fail) {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 4000,
        label: fail.message ?? 'Houve um erro em buscar os vídeos',
      });
    }
    if (data) {
      setVideos(data);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = useCallback(
    async (
      idVideo: number,
      descricao: string,
      url: string,
      flagDisponivelAssociado: boolean
    ) => {
      const { fail, response } = await api.editar({
        idVideo,
        descricao,
        url,
        idEvento: props.eventId,
        flagDisponivelAssociado,
      });
      if (fail) {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: fail.message ?? 'Houve um erro em editar o vídeo',
        });
      }
      setLoading(true);
      if (response && response.status === 200) {
        getVideos();
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getVideos, props.eventId]
  );

  const handleDelete = useCallback(
    async (idEvento: number, idVideo: number) => {
      const { fail, response } = await api.apagar(idEvento, idVideo);
      if (fail) {
        showToast({
          pauseOnFocusLoss: true,
          theme: ToastTypes.Error,
          showStatusBar: true,
          timeout: 4000,
          label: fail.message ?? 'Houve um erro em deletar o vídeo',
        });
      }

      setLoading(true);
      if (response && response.status === 200) {
        getVideos();
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getVideos]
  );

  useEffect(() => {
    getVideos();
  }, [getVideos]);

  return (
    <Modal
      title={
        props.item!?.idVideo!
          ? attachmentPrefix.Title.Editar
          : attachmentPrefix.Title.Adicionar
      }
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={props.onClose}
      size={ModalSizes.Large}
      footer={[
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <TimesCircleIcon />,
          children: attachmentPrefix.Buttons.Cancel,
          onClick: () => props.onClose!(props.modalKey),
        },
        props.registering
          ? {
              theme: ButtonThemes.Primary,
              leftIcon: <CheckCircleIcon />,
              children: attachmentPrefix.Buttons.Save,
              loading: loadingButton,
              disabled:
                loadingButton ||
                (sent && !!validator && !validator.allIsValid()) ||
                (props.item?.flagDisponivelAssociado === checked &&
                  (((props.item && props.item.url) === url &&
                    (props.item && props.item.descricao) === description) ||
                    (props.item &&
                      props.item.descricao === null &&
                      description === ''))),
              onClick: () => handleSubmit(),
            }
          : {
              theme: ButtonThemes.Primary,
              leftIcon: <CheckCircleIcon />,
              children: attachmentPrefix.Buttons.AddVideo,
              loading: loadingButton,
              disabled:
                loadingButton ||
                (sent && !!validator && !validator.allIsValid()) ||
                (props.item?.flagDisponivelAssociado === checked &&
                  (((props.item && props.item.url) === url &&
                    (props.item && props.item.descricao) === description) ||
                    (props.item &&
                      props.item.descricao === null &&
                      description === ''))),
              onClick: () => handleSubmit(),
            },
      ]}
    >
      <Row>
        <Col noPaddingTop>
          <Input
            label={attachmentPrefix.Campos.URL.Label}
            placeholder={attachmentPrefix.Campos.URL.Placeholder}
            value={url}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setUrl(e.target.value)
            }
            error={sent && !!validator && validator.urlIsValid().error}
            helperText={
              sent && !!validator ? validator.urlIsValid().helperText : ''
            }
          />
        </Col>
        <Col>
          <Input
            label={attachmentPrefix.Campos.Descricao.Label}
            placeholder={attachmentPrefix.Campos.Descricao.Placeholder}
            value={description}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setDescription(e.target.value)
            }
          />
        </Col>
        <Col noPaddingTop>
          <ControlLabel
            label={attachmentPrefix.Campos.Visibilidade}
            control={
              <Checkbox
                disabled={!url}
                value={checked}
                onChange={() => setChecked((e: boolean) => !e)}
              />
            }
            labelPosition={ControlLabelPosition.Right}
          />
        </Col>
      </Row>
      {!props.registering ? (
        <Row>
          <Collapse opened={isLoading} animateOpacity>
            <Col centralized>
              <ThreeDotsLoader />
            </Col>
          </Collapse>

          <Collapse
            opened={Boolean(videos.length) && !isLoading}
            animateOpacity
          >
            <Col style={{ marginTop: '16px' }}>
              <h4>{attachmentPrefix.EventVideo}</h4>
            </Col>
            {videos.map((video) => {
              return (
                <Col>
                  <EditableVideoItem
                    key={video.idVideo}
                    title={video.descricao}
                    titleProps={{
                      placeholder: 'Descrição do vídeo',
                      maxLength: 200,
                      validations: [
                        (title: string | null) => {
                          if (!title || !title.length!) {
                            return DEFAULT_VALIDATION_MODEL(
                              true,
                              'Campo obrigatório'
                            );
                          }
                          return DEFAULT_VALID;
                        },
                      ],
                    }}
                    flagDisponivelAssociado={video.flagDisponivelAssociado}
                    url={video.url}
                    urlProps={{
                      placeholder: 'Adicione a URL do vídeo',
                      maxLength: 200,
                      validations: [
                        (url: string) => {
                          if (!url.length) {
                            return DEFAULT_VALIDATION_MODEL(
                              true,
                              'Campo obrigatório'
                            );
                          }
                          const YOUTUBE_REGEX =
                            /(https?:\/\/)(www\.)?(youtube\.com\/(watch\?v=|live\/)([a-zA-Z0-9_-]{11})|youtu\.be\/([a-zA-Z0-9_-]{11})|m\.youtube\.com\/(watch\?v=|live\/)([a-zA-Z0-9_-]{11}))/;
                          const VIMEO_REGEX =
                            /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/;
                          if (
                            YOUTUBE_REGEX.test(url) ||
                            VIMEO_REGEX.test(url)
                          ) {
                            return DEFAULT_VALID;
                          }
                          return DEFAULT_VALIDATION_MODEL(
                            true,
                            'Informe um link válido de vídeo do YouTube ou Vimeo'
                          );
                        },
                      ],
                    }}
                    onRemoveClick={() => {
                      return new Promise(
                        (resolve: (evt?: any) => void, reject: () => void) => {
                          handleDelete(props.eventId, video.idVideo!)
                            .then(() => {
                              resolve();
                            })
                            .catch(() => {
                              reject();
                            });
                        }
                      );
                    }}
                    onEditConfirm={(
                      title: string,
                      url: string,
                      flagDisponivelAssociado: boolean
                    ) => {
                      return new Promise(
                        (resolve: (evt?: any) => void, reject: () => void) => {
                          handleEdit(
                            video.idVideo!,
                            isNullOrUndefined(title) ? '' : title,
                            isNullOrUndefined(url) ? '' : url,
                            flagDisponivelAssociado
                          )
                            .then(() => {
                              resolve();
                            })
                            .catch(() => {
                              reject();
                            });
                        }
                      );
                    }}
                  />
                </Col>
              );
            })}
          </Collapse>
        </Row>
      ) : undefined}
    </Modal>
  );
};
